import { useLazyQuery, useMutation } from '@apollo/client';
import { graphql } from 'gql.tada';
import type { ResultOf, VariablesOf } from 'gql.tada';

// The persisted documents for the invoice wizard: preview the invoices, write
// them as drafts, send those drafts to Stripe.
//
// Ruling 19: the wizard's GO calls CreateInvoiceSet and then
// ScheduleInvoiceSetInStripe back to back, so the invoices are only ever
// visible as drafts when the Stripe step failed or when the admin chose "Create
// as drafts to adjust first". Either way the Invoices card lets the admin select
// the drafts and post them (PostInvoices in usePostInvoices.ts). A schedule key
// is metadata on the rows, nothing more: there is no set-level UI.

const InvoiceSetPreviewQuery = graphql.persisted(
  "InvoiceSetPreview",
  graphql(`
    query InvoiceSetPreview($input: InvoiceSetInput!, $collectionMethod: String) {
      invoiceSetPreview(input: $input, collectionMethod: $collectionMethod) {
        dueOn
        amountCents
        memo
        lines {
          personId
          description
          amountCents
          lineType
        }
      }
    }
  `)
);

const CreateInvoiceSetMutation = graphql.persisted(
  "CreateInvoiceSet",
  graphql(`
    mutation CreateInvoiceSet($input: CreateInvoiceSetInput!) {
      createInvoiceSet(input: $input) {
        scheduleKey
        invoices {
          id
          key
          scheduleKey
          status
          amountCents
          dueDate
          memo
        }
        userErrors {
          message
          path
        }
      }
    }
  `)
);

const ScheduleInvoiceSetInStripeMutation = graphql.persisted(
  "ScheduleInvoiceSetInStripe",
  graphql(`
    mutation ScheduleInvoiceSetInStripe(
      $input: ScheduleInvoiceSetInStripeInput!
    ) {
      scheduleInvoiceSetInStripe(input: $input) {
        success
        report {
          scheduleKey
          pushedCount
          warnings
          rows {
            invoiceId
            action
            message
          }
        }
        userErrors {
          message
          path
        }
      }
    }
  `)
);

export type InvoiceSetPreviewInput = VariablesOf<typeof InvoiceSetPreviewQuery>['input'];
export type InvoiceSetInstallment = NonNullable<
  ResultOf<typeof InvoiceSetPreviewQuery>['invoiceSetPreview']
>[number];

export type InvoiceSetLine = InvoiceSetInstallment['lines'][number];

export type ScheduleInvoiceSetReport = NonNullable<
  ResultOf<typeof ScheduleInvoiceSetInStripeMutation>['scheduleInvoiceSetInStripe']
>['report'];

export {
  CreateInvoiceSetMutation,
  InvoiceSetPreviewQuery,
  ScheduleInvoiceSetInStripeMutation,
};

export function useInvoiceSetPreview() {
  return useLazyQuery(InvoiceSetPreviewQuery, { fetchPolicy: 'network-only' });
}

export function useCreateInvoiceSet() {
  return useMutation(CreateInvoiceSetMutation);
}

export function useScheduleInvoiceSetInStripe() {
  return useMutation(ScheduleInvoiceSetInStripeMutation);
}
